import React, { useEffect, useState } from 'react';
import { API, authHeaders } from '../../utils/helpers';

// ── Customer feedback settings (25 Oct) ──────────────────────────────────────
//
// Settings, Messages. The shop's own Google review link, when the "How did we
// do?" message goes, who hears about a low score, and the wording. Saved with
// the page's Save button as settings.customerFeedback (server/helpers/
// feedback.js settingsOf fills the defaults). The settings object is the
// page's own, passed in; the only state here is the staff list and the test.
//
// "Send me a test" sends the real text and email from the SAVED settings.

// Same as server/helpers/feedback.js DEFAULT_TEXT and fillText, which decide
// what is sent: written like the shop's registered texting samples (name
// first, "Reply STOP to end" last), and the link and STOP line are added if
// the wording leaves them out.
const DEFAULT_TEXT = '{shop}: Hi {first}, thanks for choosing {shop} for your bike service. How did we do? {link} Reply STOP to end.';
function fillText(template, { first, bike, shop, link }) {
  const name = String(shop || 'our shop').trim();
  let t = String(template || DEFAULT_TEXT)
    .replace(/\{shop\}:/g, `${name.replace(/\.\s*$/, '')}:`)
    .replace(/\{first\}/g, first || 'there').replace(/\{bike\}/g, bike || 'bike').replace(/\{shop\}/g, name)
    .replace(/\s+/g, ' ').trim();
  t = /\{link\}/.test(t) ? t.replace(/\{link\}/g, link) : `${t} ${link}`;
  if (!/\bSTOP\b/.test(t)) t = `${t} Reply STOP to end.`;
  return t.replace(/\s+/g, ' ').trim();
}
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const HOURS = [];
for (let h = 7; h <= 21; h++) HOURS.push(`${String(h).padStart(2, '0')}:00`);
const label12 = (t) => { const h = Number(t.slice(0, 2)); return `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'}`; };
// The same hosts as server/helpers/feedback.js GOOGLE_HOST, which is the one that decides.
const GOOGLE_HOST = /^(?:(?:www|search|maps|business)\.)?google\.(?:com|ca|co\.uk|com\.au|co\.nz|ie)$|^g\.page$|^(?:maps\.app\.)?goo\.gl$/i;
const googleOk = (v) => { try { const u = new URL(String(v || '').trim()); return u.protocol === 'https:' && GOOGLE_HOST.test(u.hostname); } catch (_) { return false; } };

export default function CustomerFeedbackSettings({ T, S, settings, setSettings, setSaved, dirty }) {
  const f = settings.customerFeedback || {};
  const cfg = {
    enabled: !!f.enabled, googleLink: f.googleLink || '', delayHours: f.delayHours ?? 2,
    windowStart: f.windowStart || '10:00', windowEnd: f.windowEnd || '19:00', cooldownDays: f.cooldownDays ?? 90,
    skipZero: f.skipZero !== false, alertUserIds: (f.alertUserIds || []).map(Number), alertEmails: f.alertEmails || [],
    textTemplate: f.textTemplate || '',
  };
  const set = (patch) => { setSettings({ ...settings, customerFeedback: { ...cfg, ...patch } }); setSaved(false); };

  const [users, setUsers] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/feedback/alert-candidates`, { credentials: 'include', headers: authHeaders() })
      .then(r => (r.ok ? r.json() : { users: [] })).then(d => setUsers(d.users || [])).catch(() => setUsers([]));
  }, []);
  const [status, setStatus] = useState(null);
  const loadStatus = () => fetch(`${API}/api/feedback/status`, { credentials: 'include', headers: authHeaders() })
    .then(r => (r.ok ? r.json() : null)).then(setStatus).catch(() => {});
  useEffect(() => { loadStatus(); }, []);
  const resume = async () => {
    await fetch(`${API}/api/feedback/resume-texts`, { method: 'POST', credentials: 'include', headers: authHeaders(true), body: '{}' }).catch(() => {});
    loadStatus();
  };
  const [typed, setTyped] = useState('');
  const addEmail = () => {
    const e = typed.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) || cfg.alertEmails.includes(e)) return;
    set({ alertEmails: [...cfg.alertEmails, e] }); setTyped('');
  };

  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const sendTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await fetch(`${API}/api/feedback/test`, { method: 'POST', credentials: 'include', headers: authHeaders(true), body: JSON.stringify({ phone: testPhone }) });
      const d = await r.json().catch(() => ({}));
      setTestResult(r.ok ? d : { error: d.error || 'The test could not be sent.' });
    } catch (_) { setTestResult({ error: 'The test could not be sent.' }); }
    setTesting(false);
  };

  const field = { padding: '6px 8px', borderRadius: 6, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontSize: 13, fontFamily: 'inherit' };
  const chip = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 10px', borderRadius: 14, fontSize: 13, color: T.text, background: T.input, border: `1px solid ${T.inputBorder}` };
  const x = (onClick, what) => <button onClick={onClick} aria-label={`Remove ${what}`} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '0 2px' }}>×</button>;
  const nameOf = (id) => ((users || []).find(u => Number(u.id) === id) || {}).name || `User ${id}`;
  const notPicked = (users || []).filter(u => !cfg.alertUserIds.includes(Number(u.id)));
  const linkOk = googleOk(cfg.googleLink);
  const preview = fillText(cfg.textTemplate, { first: 'Sam', bike: 'Trek FX 3', shop: (settings.shopProfile || {}).name || 'your shop', link: 'https://api.spinboard.app/link/feedback/…' });
  const on = cfg.enabled && linkOk;
  const n = status && status.last30;

  // Spinboard can switch the feature off for a shop (admin console): then the
  // card is not shown at all.
  if (status && status.available === false) return null;
  return (
    <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ ...S.settingsCardTitle, color: T.text }}>Customer feedback and Google reviews</div>
      <div style={{ ...S.settingsCardDesc, color: T.textSub }}>
        After a ticket is Done &amp; Paid, Spinboard texts the customer "How did we do?" (by email when there is no mobile or no texting). They rate you 0 to 10, and
        everyone who answers is offered your Google review link. A score of 0 to 6 alerts the people below, so you can call them first.
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text, marginBottom: 14, cursor: linkOk ? 'pointer' : 'default' }}>
        <input type="checkbox" checked={cfg.enabled && linkOk} disabled={!linkOk} onChange={e => set({ enabled: e.target.checked })} />
        Ask customers for feedback
        {!linkOk && <span style={{ fontSize: 11.5, color: T.textSub }}>(add your Google review link first)</span>}
      </label>
      {status && status.textsPausedAt && (
        <div style={{ fontSize: 12.5, color: T.text, background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.4)', borderRadius: 8, padding: '10px 12px', marginBottom: 14, lineHeight: 1.5 }}>
          <strong style={{ color: '#e74c3c' }}>Feedback texts are paused</strong> since {fmtWhen(status.textsPausedAt)}. {status.textsPausedReason || ''} Requests
          are going by email only, so your other texts (pickup, invoices, quotes) are not put at risk. Resume only once you know why it happened.
          <div style={{ marginTop: 8 }}>
            <button onClick={resume} style={{ ...S.btn(false), color: T.text, borderColor: T.border, fontSize: 12, padding: '5px 12px' }}>Resume feedback texts</button>
          </div>
        </div>
      )}
      {on && status && (
        <div style={{ fontSize: 11.5, color: T.textSub, marginBottom: 14, lineHeight: 1.6 }}>
          {status.watchingSince
            ? <>Asking about tickets rung up Done &amp; Paid since {fmtWhen(status.watchingSince)}.</>
            : <>Starts with the next ticket rung up Done &amp; Paid after you save.</>}
          {n && (n.sent + n.scheduled + n.skipped + n.failed) > 0 && <> Last 30 days: {n.sent} sent, {n.answered} answered, {n.scheduled} waiting, {n.skipped} skipped{n.failed ? `, ${n.failed} not sent` : ''}.</>}
          {status.lastRunNote && <div style={{ color: '#e67e22' }}>{status.lastRunNote}</div>}
        </div>
      )}

      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Google review link</span>
        <input value={cfg.googleLink} onChange={e => set({ googleLink: e.target.value.trim() })} placeholder="https://g.page/r/…/review"
          style={{ ...field, flex: 1, minWidth: 260 }} />
      </div>
      <div style={{ fontSize: 11, color: cfg.googleLink && !linkOk ? '#e74c3c' : T.textSub, margin: '-4px 0 12px' }}>
        {cfg.googleLink && !linkOk ? 'That does not look like a Google review link. ' : ''}
        In your Google Business Profile: Ask for reviews (or Get more reviews), then copy the review link.
      </div>

      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Send it</span>
        <select value={cfg.delayHours} onChange={e => set({ delayHours: Number(e.target.value) })} style={field}>
          {[1, 2, 3, 4, 6, 24].map(h => <option key={h} value={h}>{h === 24 ? 'the next day' : `${h} hour${h === 1 ? '' : 's'}`}</option>)}
        </select>
        <span style={{ ...S.thresholdUnit, color: T.textSub }}>after Done &amp; Paid, only between</span>
        <select value={cfg.windowStart} onChange={e => set({ windowStart: e.target.value })} style={field}>{HOURS.map(h => <option key={h} value={h}>{label12(h)}</option>)}</select>
        <span style={{ ...S.thresholdUnit, color: T.textSub }}>and</span>
        <select value={cfg.windowEnd} onChange={e => set({ windowEnd: e.target.value })} style={field}>{HOURS.map(h => <option key={h} value={h}>{label12(h)}</option>)}</select>
      </div>

      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Don't ask again for</span>
        <input type="number" min="0" max="365" value={cfg.cooldownDays} onChange={e => set({ cooldownDays: Math.max(0, Math.min(365, parseInt(e.target.value, 10) || 0)) })}
          style={{ ...S.thresholdInput, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text }} />
        <span style={{ ...S.thresholdUnit, color: T.textSub }}>days after asking the same customer</span>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text, marginBottom: 14, cursor: 'pointer' }}>
        <input type="checkbox" checked={cfg.skipZero} onChange={e => set({ skipZero: e.target.checked })} />
        Don't ask about $0 tickets (warranty, free adjustments)
      </label>

      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Low scores go to</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', flex: 1 }}>
          {cfg.alertUserIds.map(id => <span key={`u${id}`} style={chip}>{nameOf(id)}{x(() => set({ alertUserIds: cfg.alertUserIds.filter(v => v !== id) }), nameOf(id))}</span>)}
          {cfg.alertEmails.map(e => <span key={e} style={chip}>{e}{x(() => set({ alertEmails: cfg.alertEmails.filter(v => v !== e) }), e)}</span>)}
          {users && notPicked.length > 0 && (
            <select value="" onChange={e => e.target.value && set({ alertUserIds: [...cfg.alertUserIds, Number(e.target.value)] })} style={field}>
              <option value="">+ Add someone</option>
              {notPicked.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
          )}
          <input value={typed} onChange={e => setTyped(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
            placeholder="or type any email, then Enter" style={{ ...field, width: 220 }} />
        </div>
      </div>

      <div style={{ fontSize: 13, color: T.text, fontWeight: 600, margin: '6px 0 6px' }}>The text</div>
      <textarea value={cfg.textTemplate} onChange={e => set({ textTemplate: e.target.value })} rows={2} placeholder={DEFAULT_TEXT}
        style={{ width: '100%', boxSizing: 'border-box', background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '9px 12px', color: T.text, fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit', resize: 'vertical' }} />
      <div style={{ fontSize: 11, color: T.textSub, margin: '4px 0 4px' }}>
        {'{first}'} the customer's first name, {'{bike}'} their bike, {'{shop}'} your shop's name, {'{link}'} the link. Keep it to "how did we do", like your other service texts: the Google request belongs on the page, not in the text. The link and "Reply STOP to end" are added if you leave them out.
      </div>
      <div style={{ fontSize: 12, color: T.text, background: T.input, borderRadius: 6, padding: '8px 10px', marginBottom: 14 }}>{preview}</div>

      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
        <div style={{ fontSize: 13, color: T.text, fontWeight: 600, marginBottom: 6 }}>Send me a test</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="Your mobile (optional)" style={{ ...field, width: 180 }} />
          <button onClick={sendTest} disabled={testing || dirty} style={{ ...S.btn(false), color: T.text, borderColor: T.border, opacity: testing || dirty ? 0.5 : 1 }}>
            {testing ? 'Sending…' : 'Send me a test'}
          </button>
          <span style={{ fontSize: 11.5, color: T.textSub }}>{dirty ? 'Save first: the test uses your saved settings.' : 'The text goes to this number, the email to your login’s address. Not counted.'}</span>
        </div>
        {testResult && (
          <div style={{ fontSize: 12, marginTop: 8, color: testResult.error ? '#e74c3c' : T.text }}>
            {testResult.error || (
              <>
                {testResult.sms ? `Text: ${testResult.sms}. ` : ''}{testResult.email ? `Email to ${testResult.to.email}: ${testResult.email}. ` : 'No email address on your login. '}
                <a href={testResult.link} target="_blank" rel="noreferrer" style={{ color: T.text }}>Open the page</a>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
